import { useState } from "react";
import axios from "axios";
import { Phone, Mail, Linkedin, Instagram, ArrowUpRight } from "lucide-react";
import { CONTACT } from "../../data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  name: "",
  organisation: "",
  role: "",
  requirement: "",
  source: "",
  preferred_contact: "Email",
  email: "",
  phone: ""
};

export default function ContactSplit() {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.organisation.trim() || !form.requirement.trim()) {
      setStatus({ state: "error", message: "Name, organisation, and requirement are required." });
      return;
    }
    setStatus({ state: "loading", message: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({ state: "success", message: "Received. Every requirement gets a personal reply inside 24 hours." });
      setForm(initial);
      setTimeout(() => {
        setStatus((s) => (s.state === "success" ? { state: "idle", message: "" } : s));
      }, 8000);
    } catch (err) {
      setStatus({
        state: "error",
        message: err?.response?.data?.detail || `Something went wrong. Email ${CONTACT.email} directly.`
      });
    }
  };

  return (
    <section data-testid="contact-section" className="grid grid-cols-1 lg:grid-cols-2">
      {/* Left — Ink */}
      <div className="ground-ink text-[color:var(--paper)] py-16 md:py-20 lg:py-24 px-8 md:px-16 lg:px-20 flex flex-col justify-between gap-12">
        <div>
          <p className="stamp on-ink mb-4" style={{ color: "var(--paper)", fontSize: 12 }}>Start a discovery conversation</p>
          <h2 className="h-section" style={{ fontSize: "clamp(32px, 4vw, 56px)", lineHeight: 1.06 }} data-testid="contact-headline-1">
            One business problem is enough to begin.
          </h2>
          <p className="font-body-serif measure mt-8" style={{ fontSize: 18, lineHeight: 1.65, color: "rgba(239,231,214,0.85)" }} data-testid="contact-headline-2">
            Tell us where your people need to perform differently. We will work with you to define the audience, target behaviour, programme scope and evidence of success.
          </p>
        </div>

        <ul className="space-y-4 text-[15px]" style={{ color: "rgba(239,231,214,0.85)" }}>
          <li className="flex items-center gap-3">
            <Phone size={15} className="text-[color:var(--pink)]" />
            <a href={`tel:+91${CONTACT.phone}`} data-testid="contact-phone">+91 {CONTACT.phone}</a>
          </li>
          <li className="flex items-center gap-3">
            <Mail size={15} className="text-[color:var(--pink)]" />
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email">{CONTACT.email}</a>
          </li>
          <li className="flex items-center gap-3">
            <Linkedin size={15} className="text-[color:var(--pink)]" />
            <a href={CONTACT.linkedin} target="_blank" rel="noreferrer">linkedin.com/in/jasmeenkaur-trainer</a>
          </li>
          <li className="flex items-center gap-3">
            <Instagram size={15} className="text-[color:var(--pink)]" />
            <a href={`https://instagram.com/${CONTACT.instagram.replace("@","")}`} target="_blank" rel="noreferrer">{CONTACT.instagram}</a>
          </li>
        </ul>
      </div>

      {/* Right — Signal Blue */}
      <div className="py-16 md:py-20 lg:py-24 px-8 md:px-16 lg:px-20 flex items-center" style={{ background: "var(--signal)" }}>
        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="ground-paper w-full max-w-[600px] p-8 md:p-10 border-[1.5px] border-[color:var(--ink)]"
        >
          <span className="stamp" style={{ fontSize: 12 }}>Brief · 001</span>
          <h3 className="font-hook mt-4" style={{ fontSize: 28, lineHeight: 1.08, letterSpacing: "-0.015em" }}>
            Start a discovery conversation.
          </h3>

          <div className="mt-8 space-y-6">
            <div>
              <label className="field-lbl" htmlFor="cf-name">Your name *</label>
              <input id="cf-name" data-testid="contact-input-name" className="field" value={form.name} onChange={(e) => update("name", e.target.value)} required />
            </div>
            <div>
              <label className="field-lbl" htmlFor="cf-org">Organisation *</label>
              <input id="cf-org" data-testid="contact-input-organisation" className="field" value={form.organisation} onChange={(e) => update("organisation", e.target.value)} required />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="field-lbl" htmlFor="cf-role">Your role</label>
                <input id="cf-role" data-testid="contact-input-role" className="field" value={form.role} onChange={(e) => update("role", e.target.value)} />
              </div>
              <div>
                <label className="field-lbl" htmlFor="cf-email">Work email</label>
                <input id="cf-email" type="email" data-testid="contact-input-email" className="field" value={form.email} onChange={(e) => update("email", e.target.value)} />
              </div>
            </div>
            <div>
              <label className="field-lbl" htmlFor="cf-req">What are you trying to solve? *</label>
              <textarea id="cf-req" data-testid="contact-input-requirement" rows={4} className="field resize-none" placeholder="Keep it brief — we'll ask the right questions in the follow-up" value={form.requirement} onChange={(e) => update("requirement", e.target.value)} required />
            </div>
            <div>
              <label className="field-lbl" htmlFor="cf-source">How did you hear about us?</label>
              <select id="cf-source" data-testid="contact-input-source" className="field" value={form.source} onChange={(e) => update("source", e.target.value)}>
                <option value="">Select</option>
                <option value="LinkedIn">LinkedIn</option>
                <option value="Google">Google</option>
                <option value="Referral">Referral</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <span className="field-lbl">Preferred contact</span>
              <div className="flex gap-6 mt-3">
                {["Email", "WhatsApp", "Phone"].map((opt) => (
                  <label key={opt} className="flex items-center gap-2 text-[15px] cursor-pointer">
                    <input
                      type="radio"
                      name="preferred_contact"
                      value={opt}
                      checked={form.preferred_contact === opt}
                      onChange={(e) => update("preferred_contact", e.target.value)}
                      className="accent-[#2B2BD4]"
                      data-testid={`contact-radio-${opt.toLowerCase()}`}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {status.state === "success" && (
            <p data-testid="contact-success" className="mt-8 p-4 border-l-[3px] border-[color:var(--signal)] bg-[color:var(--lab)] text-[14px]">
              {status.message}
            </p>
          )}
          {status.state === "error" && (
            <p data-testid="contact-error" className="mt-8 p-4 border-l-[3px] border-[color:var(--pink)] bg-[color:var(--margin)] text-[14px]">
              {status.message}
            </p>
          )}

          <button type="submit" disabled={status.state === "loading"} data-testid="contact-submit" className="btn-ink mt-10 w-full justify-center">
            {status.state === "loading" ? "Sending..." : (<>Start a discovery conversation <ArrowUpRight size={16} /></>)}
          </button>
        </form>
      </div>
    </section>
  );
}
